import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

type Theme = 'light' | 'dark' | 'system';

const NEXT: Record<Theme, Theme> = { light: 'dark', dark: 'system', system: 'light' };

function applyGuestTheme(theme: Theme) {
  const html = document.documentElement;
  if (theme === 'dark') {
    html.classList.add('dark');
  } else if (theme === 'light') {
    html.classList.remove('dark');
  } else {
    window.matchMedia('(prefers-color-scheme: dark)').matches
      ? html.classList.add('dark')
      : html.classList.remove('dark');
  }
}

function SunIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="4" />
      <path strokeLinecap="round" d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path strokeLinecap="round" d="M8 21h8M12 17v4" />
    </svg>
  );
}

const ICONS: Record<Theme, React.ReactNode> = {
  light: <SunIcon />,
  dark: <MoonIcon />,
  system: <SystemIcon />,
};

const LABELS: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

export default function ThemeToggle({ className = '' }: { className?: string }) {
  const { user, updateSettings } = useAuth();

  const [theme, setTheme] = useState<Theme>(() => {
    if (user?.settings.theme) return user.settings.theme;
    return (localStorage.getItem('theme') as Theme | null) ?? 'system';
  });

  // Sync when user logs in
  useEffect(() => {
    if (user?.settings.theme) setTheme(user.settings.theme);
  }, [user?.settings.theme]);

  function toggle() {
    const next = NEXT[theme];
    setTheme(next);
    if (user) {
      updateSettings({ theme: next }).catch(() => {});
    } else {
      localStorage.setItem('theme', next);
      applyGuestTheme(next);
    }
  }

  return (
    <button
      onClick={toggle}
      title={`Theme: ${LABELS[theme]}`}
      className={`rounded-lg p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${className}`}
    >
      {ICONS[theme]}
    </button>
  );
}
